import styled from "@emotion/styled";
import { padding } from "polished";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { blocksToText } from "@brexhq/shared/utils/sanity/blocksToText";

import type { LinkListItemProps } from "./LinkListItem";

const ListItemLink = styled(Link, {
  shouldForwardProp: (prop) =>
    !["hasIcon", "isInline", "isOutlined"].includes(prop),
})<{
  hasIcon?: boolean;
  isInline?: boolean;
  isOutlined?: boolean;
}>(({ theme, hasIcon, isInline, isOutlined }) => {
  const { sml, xsml, xxsml } = theme.spacing.LRG;

  const outlinedProps = {
    ...padding(sml, xsml, sml, sml),
    border: `1px solid ${theme.colors.border.default}`,
    background: theme.colors.background.primary,
  };

  return {
    display: isInline ? "inline-flex" : "flex",
    padding: isInline || !hasIcon ? undefined : xxsml / 2,
    borderRadius: theme.borders.radius.default,
    alignItems: "center",
    gap: xsml,
    transition: `background-color ${theme.animations.durationSlow}`,
    outlineOffset: -1,
    ...(isOutlined ? outlinedProps : {}),

    "&:hover": {
      background:
        hasIcon || isOutlined ? theme.colors.border.default : undefined,
      "[data-name='IconContainer']": {
        color: theme.colors.accent.primary.background.default,
        backgroundColor: theme.colors.background.secondary,
      },
      "[data-name='LinkText']": {
        color: !hasIcon
          ? theme.colors.accent.primary.background.default
          : undefined,
      },
    },

    [theme.breakpoints.nav.SML]: {
      display: "none",
    },
  };
});

const LinkText = styled.span(({ theme }) => ({
  ...theme.typography.BodySmallSB,
  color: theme.colors.content.primary,
  [theme.breakpoints.nav.MED]: {
    fontSize: 12,
  },
}));

const Subtitle = styled.span(({ theme }) => ({
  ...theme.typography.BodySmallM,
  color: theme.colors.content.tertiary,
  lineHeight: "16px",
  [theme.breakpoints.nav.MED]: {
    fontSize: 12,
  },
}));

const IconContainer = styled.div<{
  isOutlined?: boolean;
}>(({ theme, isOutlined }) => ({
  display: "flex",
  alignItems: "center",
  color: theme.colors.content.primary,
  padding: theme.spacing.LRG.xsml,
  border: "1px solid",
  borderColor: isOutlined ? theme.colors.border.default : "transparent",
  background: isOutlined
    ? theme.colors.background.primary
    : theme.colors.background.secondary,
  borderRadius: theme.borders.radius.default,
  svg: {
    height: 24,
    width: 24,
  },
}));

export default function LinkListItemDesktop({
  link: { text, link, subtitle, subtitleV2Raw, icon },
  linkModifier,
  modifier,
}: LinkListItemProps) {
  const subtitleContent =
    subtitleV2Raw && subtitleV2Raw?.length > 0
      ? blocksToText(subtitleV2Raw)
      : subtitle;
  return (
    <ListItemLink
      modifier="unstyled"
      to={link?.to || link?.toExternal || link?.hash || "#"}
      noFollow={link.noFollow}
      label={text}
      component="Navigation LinkListModule (desktop)"
      hasIcon={!!icon}
      isOutlined={modifier === "outline"}
      isInline={!modifier && !icon && !subtitle}
      // tabbing will be controlled by the arrow keys, orchestrated by the nav container
      tabIndex={-1}
    >
      {icon && (
        <IconContainer
          data-name="IconContainer"
          isOutlined={modifier !== "outline"}
          dangerouslySetInnerHTML={{ __html: icon.mono }}
        />
      )}
      <Container display="flex" direction="column" style={{ gap: 4 }}>
        <LinkText data-name="LinkText">
          {text}
          {linkModifier === "arrow" && (
            <span data-name="Arrow" aria-hidden="true">
              {" "}
              &rarr;
            </span>
          )}
        </LinkText>
        {subtitleContent && <Subtitle>{subtitleContent}</Subtitle>}
      </Container>
    </ListItemLink>
  );
}
